import { type Deal, type DealApi, type DealParticipantApi, type DealState, pmpDealType } from '@openx/types/deal.ts';
import { BUDGET_MODE } from '@openx/types/monetizing.ts';
import cloneDeep from 'lodash/cloneDeep';

export const sanitizeDealData = (
  deal: Partial<DealState>,
  dealId: string | undefined,
  isPacingEnabled?: boolean
): { dealSanitized: Partial<DealApi>; deal_id_prefix?: string | null } => {
  switch (deal.pmp_deal_type) {
    case pmpDealType.PreferredDeal:
      delete deal.use_market_floor;
      delete deal.lifetime_impression_goal;
      break;
    case pmpDealType.WithinOpenAuction:
      delete deal.lifetime_impression_goal;
      break;
    case pmpDealType.PrivateAuction:
      delete deal.lifetime_impression_goal;
      break;
    case pmpDealType.Guaranteed:
      delete deal.use_market_floor;
      break;
  }

  let deal_id_prefix: string | null | undefined;

  if (!dealId) {
    if (deal.deal_id_type_radio === 'GENERATED') {
      deal_id_prefix = deal.generated_deal_id_prefix === 'Default' ? null : deal.generated_deal_id_prefix;
      delete deal.deal_id;
    } else if (deal.deal_id_type_radio === 'CUSTOM') {
      deal.deal_id = deal.custom_deal_id ?? undefined;
    } else if (deal.deal_id_type_radio === 'EXTERNAL') {
      deal.deal_id = deal.external_deal_id ?? undefined;
    }
  }

  delete deal.generated_deal_id_prefix;
  delete deal.custom_deal_id;
  delete deal.external_deal_id;
  delete deal.deal_id_type_radio;
  delete deal.modified_date;
  delete deal.revision;
  delete deal.synchronized_with;
  delete deal.has_multiprovider_audience;

  if (isPacingEnabled) {
    const {
      is_pacing_enabled: _is_pacing_enabled,
      monetizing_budget,
      pacing_mode,
      monetizing_region,
      budget_type,
      budget_mode,
      cadence_type,
      ...dealRest
    } = deal;

    const dealSanitized = {
      ...dealRest,
      ...(deal.start_date !== undefined && { flight_start: deal.start_date }),
      ...(deal.end_date !== undefined && { flight_end: deal.end_date }),
      ...(pacing_mode && { pacing_mode: pacing_mode }),
      ...(monetizing_region && { region: monetizing_region }),
      ...(budget_type && { budget_type: budget_type }),
      ...(budget_mode === BUDGET_MODE.CADENCE &&
        monetizing_budget && {
          budget: null,
          cadences: [{ budget: monetizing_budget.replaceAll(' ', ''), type: cadence_type }],
        }),
      ...(budget_mode === BUDGET_MODE.CAMPAIGN_FLIGHT_TIME &&
        monetizing_budget && { budget: monetizing_budget.replaceAll(' ', ''), cadences: [] }),
    } as Partial<DealApi>;

    return { dealSanitized, deal_id_prefix };
  } else {
    delete deal.is_pacing_enabled;
    delete deal.pacing_mode;
    delete deal.monetizing_budget;
    delete deal.monetizing_region;
    delete deal.budget_type;
    delete deal.budget_mode;
    delete deal.cadence_type;
  }

  deal.deal_participants = deal.deal_participants?.map(participant => ({
    ...participant,
    buyer_ids: participant.buyer_ids ?? [],
  }));

  return { dealSanitized: deal as Partial<DealApi>, deal_id_prefix };
};

export const mapApiDealParticipants = (
  apiDeal: DealApi,
  deal: Partial<Deal>,
  demandPartnerIds: Record<string, string>
) => {
  if (!deal.deal_participants) {
    return apiDeal;
  }

  const { dv360DemandPartnerId } = demandPartnerIds;

  apiDeal.deal_participants = deal.deal_participants.map(participant => {
    const { brand_ids, demand_partner, main_buyer_id, buyer_id, buyer_ids: currentBuyerIds } = participant;
    let buyer_ids = currentBuyerIds ? [...currentBuyerIds] : [];

    const isDV360Participant = demand_partner === dv360DemandPartnerId;
    if (buyer_id && isDV360Participant && !deal.enable_dv360_inventory_package) {
      buyer_ids = [buyer_id];
    }

    if (main_buyer_id) {
      buyer_ids.push(main_buyer_id);
    }

    return {
      brand_ids,
      buyer_ids: participant.amazon_sync_marketplace_enabled ? [] : buyer_ids,
      demand_partner,
      main_buyer_id,
    } as unknown as DealParticipantApi;
  });

  return apiDeal;
};

export const dealStateToApi = (
  deal: Partial<Deal>,
  dealId: string | undefined,
  demandPartnerIds: Record<string, string>,
  isPacingEnabled?: boolean
): {
  apiDeal: DealApi;
  deal_id_prefix: string | null | undefined;
} => {
  const { dealSanitized, deal_id_prefix } = sanitizeDealData(cloneDeep(deal), dealId, isPacingEnabled);

  if (dealSanitized.use_market_floor !== undefined) {
    dealSanitized.use_market_floor = deal.use_market_floor ? '1' : '0';
  }

  const apiDealWithMappedParticipants = mapApiDealParticipants(dealSanitized as DealApi, deal, demandPartnerIds);

  return { apiDeal: apiDealWithMappedParticipants, deal_id_prefix };
};
